import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { MenuItem } from './types';
import { cn } from '@/lib/utils';

interface SidebarMenuItemProps {
  item: MenuItem;
  isCollapsed: boolean;
  level?: number;
}

// A menu item's review count = its own badge plus its children's (so a collapsed
// group icon surfaces the count of its hidden sub-items, e.g. discovery).
const badgeCount = (item: MenuItem): number => {
  const own = typeof item.badge === 'number' ? item.badge : Number(item.badge) || 0;
  const children = (item.children ?? []).reduce((sum, c) => sum + badgeCount(c), 0);
  return own + children;
};

// Red review pill, right-aligned inline (expanded rows).
const ReviewBadge = ({ count }: { count: number }) => (
  <span className="ml-auto inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white">
    {count}
  </span>
);

// Red review pill pinned to the top-right corner (collapsed icons).
const CornerBadge = ({ count }: { count: number }) => (
  <span className="absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-semibold leading-none text-white ring-2 ring-background">
    {count}
  </span>
);

export const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
  item,
  isCollapsed,
  level = 0
}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const label = t(item.labelKey);

  const isActive = (path?: string, exact?: boolean) => {
    if (!path) return false;
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isParentActive = (children?: MenuItem[]) => {
    if (!children) return false;
    return children.some(child => isActive(child.path, child.exact));
  };

  const IconComponent = item.icon;

  if (isCollapsed && level === 0) {
    const linkPath = item.path || (item.children && item.children[0]?.path);

    if (item.disabled) {
      return (
        <Button
          variant="ghost"
          className="h-8 w-8 p-0 text-muted-foreground/40 cursor-not-allowed"
          disabled
        >
          <IconComponent className="h-4 w-4" />
          <span className="sr-only">{label}</span>
        </Button>
      );
    }

    if (linkPath) {
      const count = badgeCount(item);
      return (
        <Button
          variant="ghost"
          data-tour-id={`sidebar-${item.id}`}
          className={cn(
            "relative h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors",
            isActive(linkPath, item.exact) && "bg-muted text-foreground"
          )}
          asChild
        >
          <Link to={linkPath}>
            <IconComponent className="h-4 w-4" />
            {count > 0 && <CornerBadge count={count} />}
            <span className="sr-only">{label}</span>
          </Link>
        </Button>
      );
    }

    return (
      <Button
        variant="ghost"
        className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
      >
        <IconComponent className="h-4 w-4" />
        <span className="sr-only">{label}</span>
      </Button>
    );
  }

  // Expanded view
  if (!isCollapsed) {
    if (!item.children || item.children.length === 0) {
      return (
        <div className={cn("px-3", level > 0 && "pl-8")} data-tour-id={level === 0 ? `sidebar-${item.id}` : undefined}>
          <Button
            variant="ghost"
            className={cn(
              "w-full justify-start px-3 gap-2.5 h-8 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors",
              isActive(item.path, item.exact) && "bg-muted text-foreground font-medium",
              item.disabled && "text-muted-foreground/40 cursor-not-allowed hover:bg-transparent hover:text-muted-foreground/40"
            )}
            disabled={item.disabled}
            asChild={!!item.path && !item.disabled}
          >
            {item.path && !item.disabled ? (
              <Link to={item.path} className="flex items-center gap-2.5">
                <IconComponent className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm truncate">{label}</span>
                {badgeCount(item) > 0 && <ReviewBadge count={badgeCount(item)} />}
              </Link>
            ) : (
              <div className="flex items-center gap-2.5">
                <IconComponent className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm truncate">{label}</span>
                {badgeCount(item) > 0 && <ReviewBadge count={badgeCount(item)} />}
              </div>
            )}
          </Button>
        </div>
      );
    }

    // Accordion (has children)
    if (item.disabled) {
      return (
        <div className="text-muted-foreground/40 px-3">
          <div className="px-3 py-2 cursor-not-allowed">
            <div className="flex items-center gap-2.5">
              <IconComponent className="h-4 w-4" />
              <span className="text-sm">{label}</span>
              {badgeCount(item) > 0 && <ReviewBadge count={badgeCount(item)} />}
            </div>
          </div>
        </div>
      );
    }

    return (
      <AccordionItem value={item.id} className="border-none" data-tour-id={`sidebar-${item.id}`}>
        <AccordionTrigger
          className={cn(
            "group/acc px-3 mx-3 rounded-md py-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 hover:no-underline transition-colors [&[data-state=open]>svg]:rotate-90",
            isParentActive(item.children) && "bg-muted/50 text-foreground"
          )}
        >
          <div className="flex flex-1 items-center gap-2.5">
            <IconComponent className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">{label}</span>
            {/* Aggregated child count rolls up onto the group when the accordion
                is closed; once open, the child rows (e.g. discovery) carry it. */}
            {badgeCount(item) > 0 && (
              <span className="ml-auto mr-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white group-data-[state=open]/acc:hidden">
                {badgeCount(item)}
              </span>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="pb-1 pt-0.5">
          <div className="space-y-0.5">
            {item.children.map((child) => (
              <SidebarMenuItem
                key={child.id}
                item={child}
                isCollapsed={false}
                level={level + 1}
              />
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  }

  return null;
};
